import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { CameraActivity } from '../components/CameraActivity';
import type { ActivityEpisode } from '../lib/api';
import { useSearchParams } from 'react-router-dom';
import { isStaff, useAuth } from '../hooks/useAuth';
import { api, formatWib, type Camera } from '../lib/api';
import { useBahasa } from '../i18n';
import { NomorHalaman } from '../components/Pager';

/**
 * Pilihan jumlah baris per halaman (§D7.3).
 *
 * Diminta lapangan 23 Agu 2026: 100 baris sekaligus memang bagus untuk menyapu
 * seharian, tapi orang yang cuma mau tahu "apa yang barusan terjadi" harus
 * menggulung jauh untuk menemukan ujung halamannya. Nilainya tersimpan di
 * `localStorage` — ini preferensi cara membaca, bukan keadaan aplikasi, dan
 * tidak layak dibawa ke akun.
 */
const PILIHAN_PAGE_SIZE = [5, 10, 25, 50, 100] as const;
const PAGE_SIZE_DEFAULT = 25;
const KUNCI_PAGE_SIZE = 'lensa.logs.pageSize';

function pageSizeAwal(): number {
  try {
    const n = Number(localStorage.getItem(KUNCI_PAGE_SIZE));
    return (PILIHAN_PAGE_SIZE as readonly number[]).includes(n) ? n : PAGE_SIZE_DEFAULT;
  } catch {
    // Mode privat / penyimpanan diblokir: bawaannya tetap jalan.
    return PAGE_SIZE_DEFAULT;
  }
}

type Level = 'debug' | 'info' | 'warn' | 'error';

interface LogRow {
  id: string;
  cameraId: string;
  cameraName: string | null;
  level: Level;
  event: string;
  message: string;
  occurredAt: string;
  lastOccurredAt: string;
  occurrenceCount: number;
  detail?: unknown;
}

interface LogPage {
  logs: LogRow[];
  total: number;
  page: number;
  pageCount: number;
}

interface Summary {
  windowHours: number;
  cameraCount: number;
  cameras: { cameraId: string; cameraName: string | null; errorRows: number }[];
  /**
   * Keadaan **sekarang** — kamera aktif yang statusnya bukan `online` (§D7.3).
   *
   * Angka yang sama persis dengan badge di item Logs pada navigasi (§G9), dan
   * sengaja bukan angka yang sama dengan `cameraCount` di atasnya. Badge
   * menjawab "ada yang rusak sekarang?", jendela 24 jam menjawab "apa yang
   * terjadi seharian ini?".
   */
  now: {
    cameraCount: number;
    cameras: {
      cameraId: string;
      cameraName: string;
      status: 'offline' | 'error';
      lastError: string | null;
    }[];
  };
}

/**
 * Warna level §G3. Setiap warna selalu berpasangan dengan label teks — status
 * tidak boleh disampaikan hanya lewat warna (§G3.1).
 */
const LEVEL_STYLE: Record<Level, { chip: string; label: string }> = {
  error: { chip: 'bg-red-600 text-white', label: 'ERROR' },
  warn: { chip: 'bg-amber-500 text-black', label: 'WARN' },
  info: { chip: 'bg-subtle text-muted', label: 'INFO' },
  debug: { chip: 'bg-subtle text-muted', label: 'DEBUG' },
};

function jamLalu(hours: number): string {
  return new Date(Date.now() - hours * 3600 * 1000).toISOString();
}

/**
 * Satu kartu ringkasan yang bisa dilipat, di atas tabel (§D7.3).
 *
 * Dipakai dua kali dengan angka yang berbeda arti — "sedang rusak" dan "sempat
 * error 24 jam terakhir" — jadi bentuknya satu komponen, bukan dua salinan
 * markup yang sama. Isinya dilipat karena terbentang keduanya bisa jadi puluhan
 * nama yang mendorong tabel, yaitu isi halaman ini, jauh ke bawah lipatan.
 */
function KartuRingkas({
  chip,
  kalimat,
  terbuka,
  onToggle,
  children,
}: {
  chip: { chip: string; label: string };
  kalimat: React.ReactNode;
  terbuka: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-3 rounded-lg border border-line bg-subtle text-sm">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={terbuka}
        className="flex min-h-tap w-full items-center gap-2 p-3 text-left"
      >
        <span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${chip.chip}`}>
          {chip.label}
        </span>
        <span className="min-w-0 flex-1">{kalimat}</span>
        <span
          aria-hidden="true"
          className={`shrink-0 transition-transform duration-200 ease-out motion-reduce:transition-none ${
            terbuka ? 'rotate-90' : ''
          }`}
        >
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="m9 6 6 6-6 6" />
          </svg>
        </span>
      </button>

      {terbuka && <div className="border-t border-line px-3 py-2">{children}</div>}
    </div>
  );
}

type Tab = 'aktivitas' | 'teknis';

/** Rentang yang ditawarkan filter, urut naik — dipakai juga oleh `lihatTeknis`. */
const PILIHAN_JAM = [1, 24, 72, 168, 720] as const;

const KUNCI_TAB = 'lensa.logs.tab';

function bacaTab(): Tab {
  try {
    const v = localStorage.getItem(KUNCI_TAB);
    if (v === 'teknis' || v === 'aktivitas') return v;
  } catch {
    /* penyimpanan diblokir — pakai bawaan */
  }
  return 'aktivitas';
}

export function LogsPage() {
  const { user } = useAuth();
  const { t } = useBahasa();
  /**
   * Operator melihat sama seperti admin di halaman ini (§D7.3, §F8): dialah
   * yang memasang kameranya, dan mendiagnosis tanpa `stderr` aslinya cuma
   * tebakan. Yang membedakan keduanya ada di halaman Cameras, bukan di sini.
   */
  const isAdmin = isStaff(user);
  const [params, setParams] = useSearchParams();

  const [cameras, setCameras] = useState<Camera[]>([]);
  const [data, setData] = useState<LogPage | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  /** Daftar nama kamera di panel ringkasan — tertutup sampai diminta (§D7.3). */
  const [ringkasanTerbuka, setRingkasanTerbuka] = useState(false);
  /** Idem, untuk kartu "sedang offline". Dilipat sendiri: dua kartu yang buka
      bersamaan mendorong tabel lebih jauh daripada panel tunggal yang dulu. */
  const [sekarangTerbuka, setSekarangTerbuka] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [terbuka, setTerbuka] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(pageSizeAwal);

  // Default §D7.3: 24 jam terakhir, hanya warn dan error. Halaman log yang
  // dibuka langsung penuh baris info tidak menolong siapa pun mencari masalah.
  const [levels, setLevels] = useState<Level[]>(['warn', 'error']);
  const [jam, setJam] = useState(24);
  const [q, setQ] = useState('');

  /**
   * Dua tab (§G13): **Aktivitas** (bawaan, semua peran) dan **Teknis** (tabel
   * §D7.3, Admin + Operator).
   *
   * Menghapus tampilan teknis akan mencabut satu-satunya tempat `stderr`
   * ffmpeg bisa dibaca — dan itu yang dipakai persis saat kalimat manusiawi di
   * tab pertama ternyata tidak cukup. Jadi ia dipindah, bukan dibuang.
   *
   * Pilihannya menempel ke **browser**, bukan ke akun: ini soal pekerjaan yang
   * sedang dilakukan, bukan soal siapa orangnya. `?tab=` di URL menang atas
   * ingatan itu, supaya tautan yang dikirim orang mendarat di tab yang dia
   * maksud.
   */

  function pilihTab(next: Tab) {
    try {
      localStorage.setItem(KUNCI_TAB, next);
    } catch {
      // Tidak bisa diingat bukan alasan untuk tidak berpindah.
    }
    const sp = new URLSearchParams(params);
    sp.set('tab', next);
    setParams(sp, { replace: true });
  }

  /**
   * Dari satu episode ke baris mentahnya (§G13).
   *
   * Rentang di halaman ini dipilih dari daftar tetap (1 jam … 30 hari), bukan
   * dari dua stempel waktu, jadi yang bisa dijanjikan tautan ini adalah rentang
   * **terkecil yang masih memuat** episode itu — bukan potongan persis di
   * sekitarnya. Membawa orang ke puncak tabel tanpa filter apa pun jauh lebih
   * buruk: di sanalah dia harus menebak sendiri baris mana yang tadi dibacanya.
   */
  function lihatTeknis(cameraId: string, ep: ActivityEpisode) {
    const umurJam = (Date.now() - new Date(ep.startedAt).getTime()) / 3_600_000;
    const cocok = PILIHAN_JAM.find((h) => h >= umurJam) ?? PILIHAN_JAM[PILIHAN_JAM.length - 1]!;
    setJam(cocok);
    setLevels(['debug', 'info', 'warn', 'error']);
    setQ('');
    const sp = new URLSearchParams(params);
    sp.delete('camera_id');
    sp.append('camera_id', cameraId);
    sp.set('tab', 'teknis');
    setParams(sp);
  }

  // `?camera_id=` datang dari tombol "Lihat Log" di Camera Page (§G9).
  const cameraFilter = useMemo(() => params.getAll('camera_id'), [params]);

  const tabAktif: Tab = useMemo(() => {
    // Viewer tidak punya tab Teknis sama sekali (§D7.3) — bukan karena
    // tombolnya disembunyikan, tapi karena keadaannya memang tidak ada
    // baginya. Tanpa penjepit ini, `?tab=teknis` yang ditempel dari tautan
    // orang lain (atau nilai basi di localStorage) membuat halamannya menarik
    // tabel log yang tidak akan pernah dirender.
    if (!isAdmin) return 'aktivitas';
    const dariUrl = params.get('tab');
    if (dariUrl === 'teknis' || dariUrl === 'aktivitas') return dariUrl;
    return bacaTab();
  }, [params, isAdmin]);

  useEffect(() => {
    void api<{ cameras: Camera[] }>('/cameras')
      .then((r) => setCameras(r.cameras))
      .catch(() => undefined);
  }, []);

  const muat = useCallback(async () => {
    // Tab Aktivitas tidak memakai tabel ini sama sekali. Menariknya tetap
    // berarti satu query per pergantian filter pada halaman yang memang dibuka
    // sepanjang hari — dibayar untuk jawaban yang tidak pernah dilihat.
    if (tabAktif === 'aktivitas') return;
    try {
      const sp = new URLSearchParams();
      for (const id of cameraFilter) sp.append('camera_id', id);
      for (const l of levels) sp.append('level', l);
      sp.set('from', jamLalu(jam));
      if (q.trim()) sp.set('q', q.trim());
      sp.set('page', String(page));
      sp.set('limit', String(pageSize));

      setData(await api<LogPage>(`/logs?${sp}`));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [cameraFilter, levels, jam, q, page, pageSize, tabAktif]);

  useEffect(() => {
    void muat();
  }, [muat]);

  useEffect(() => {
    const ambil = () =>
      void api<Summary>('/logs/summary')
        .then(setSummary)
        .catch(() => undefined);
    ambil();
    const t = window.setInterval(ambil, 60_000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => setPage(0), [cameraFilter, levels, jam, q, pageSize]);

  function toggleLevel(l: Level) {
    setLevels((cur) => (cur.includes(l) ? cur.filter((x) => x !== l) : [...cur, l]));
  }

  /**
   * Satu kamera saja, atau semuanya.
   *
   * Dulu berupa deretan chip yang bisa dipilih banyak sekaligus; dengan 96
   * kamera bilah filternya jadi lebih tinggi daripada tabel yang difilternya.
   * URL-nya tetap `camera_id` berulang supaya tautan lama dari Camera Page
   * tidak putus — cuma UI-nya yang menyempit jadi satu pilihan.
   */
  function pilihKamera(id: string) {
    const next = new URLSearchParams(params);
    next.delete('camera_id');
    if (id) next.append('camera_id', id);
    setParams(next);
  }

  const exportHref = (() => {
    const sp = new URLSearchParams();
    for (const id of cameraFilter) sp.append('camera_id', id);
    for (const l of levels) sp.append('level', l);
    sp.set('from', jamLalu(jam));
    if (q.trim()) sp.set('q', q.trim());
    return `/api/v1/logs/export?${sp}`;
  })();

  const levelPilihan: Level[] = isAdmin
    ? ['debug', 'info', 'warn', 'error']
    : ['info', 'warn', 'error'];

  return (
    <div className="mx-auto max-w-6xl p-4">
      <h1 className="mb-4 text-xl">{t('Event log')}</h1>

      {/* Ringkasan di atas tabel (§D7.3): berapa kamera bermasalah 24 jam
          terakhir, dengan tautan langsung ke kameranya. */}
      {/* Daftar kameranya dilipat (§D7.3).
          Terbentang, ia 17 nama yang mendorong tabel — isi halaman ini — jauh ke
          bawah lipatan, setiap kali halaman dibuka, dan isinya nyaris tidak
          pernah berubah dari kemarin. Yang perlu terbaca sekali lihat cuma satu
          kalimat: ada error, sebanyak ini, dalam 24 jam terakhir. Namanya
          menyusul kalau ditanya.

          Penanda ERROR memakai chip yang sama persis dengan kolom "Level" di
          tabel bawah — satu kosakata untuk satu arti, bukan dua cara berbeda
          menyebut hal yang sama di satu halaman. */}
      {/* Kartu "sekarang" berdiri di atas kartu 24 jam dengan sengaja.
          Angkanya adalah angka yang sama dengan badge di navigasi (§G9), dan
          badge itulah yang membawa orang ke halaman ini: yang pertama dicari
          begitu halamannya terbuka adalah "yang mana yang mati", bukan "apa
          yang terjadi kemarin". Sampai sebelum ini badge menyebut angka yang
          tidak muncul di mana pun di dalam halaman yang ditunjuknya — orang
          mengeklik "3" lalu mendarat di panel yang menulis "17".

          Keduanya tidak pernah digabung jadi satu angka: kamera yang mati
          barusan belum tentu sempat menulis baris error, dan kamera yang sudah
          pulih tetap punya riwayat. Menjumlahkannya menghasilkan angka yang
          tidak menjawab pertanyaan mana pun. */}
      {summary && summary.now.cameraCount > 0 && (
        <KartuRingkas
          chip={LEVEL_STYLE.error}
          terbuka={sekarangTerbuka}
          onToggle={() => setSekarangTerbuka((v) => !v)}
          kalimat={
            <>
              <strong>{summary.now.cameraCount}</strong>{' '}
              {t('cameras are offline right now')}
            </>
          }
        >
          {summary.now.cameras.map((c, i) => (
            <span key={c.cameraId}>
              {i > 0 && ', '}
              <button
                type="button"
                onClick={() => {
                  const next = new URLSearchParams(params);
                  next.delete('camera_id');
                  next.append('camera_id', c.cameraId);
                  setParams(next);
                }}
                className="text-accent-text underline underline-offset-2"
                title={c.lastError ?? undefined}
              >
                {c.cameraName}
              </button>
              {/* Statusnya ditulis, bukan diwarnai (§G3.1). "Offline" dan
                  "Error" dua keadaan berbeda: yang satu tidak menjawab, yang
                  satu menjawab dengan keluhan. */}
              {c.status === 'error' && <span className="text-muted"> {t('(error)')}</span>}
            </span>
          ))}
        </KartuRingkas>
      )}

      {summary && summary.cameraCount > 0 && (
        <KartuRingkas
          chip={LEVEL_STYLE.error}
          terbuka={ringkasanTerbuka}
          onToggle={() => setRingkasanTerbuka((v) => !v)}
          kalimat={
            <>
              <strong>{summary.cameraCount}</strong>{' '}
              {t('cameras reported in the last {jam} hours', { jam: summary.windowHours })}
            </>
          }
        >
          {summary.cameras.map((c, i) => (
            <span key={c.cameraId}>
              {i > 0 && ', '}
              <button
                type="button"
                onClick={() => {
                  const next = new URLSearchParams(params);
                  next.delete('camera_id');
                  next.append('camera_id', c.cameraId);
                  setParams(next);
                  setLevels(['error']);
                }}
                className="text-accent-text underline underline-offset-2"
              >
                {c.cameraName ?? t('Camera {id}', { id: c.cameraId })} ({c.errorRows})
              </button>
            </span>
          ))}
        </KartuRingkas>
      )}

      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-line bg-surface p-3">
        <label className="flex flex-col gap-1 text-sm">
          {t('Camera')}
          <select
            value={cameraFilter[0] ?? ''}
            onChange={(e) => pilihKamera(e.target.value)}
            className="min-h-tap w-56 rounded-md border border-line bg-surface px-2"
          >
            <option value="">{t('All cameras')}</option>
            {cameras.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        {tabAktif === 'teknis' && (
        <div className="flex flex-col gap-1">
          <span className="text-sm">{t('Level')}</span>
          <div className="flex gap-1">
            {levelPilihan.map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => toggleLevel(l)}
                aria-pressed={levels.includes(l)}
                className={`min-h-tap rounded-md border px-3 text-sm uppercase transition-[background-color,scale] duration-150 active:scale-[0.96] ${
                  levels.includes(l)
                    ? 'border-accent bg-subtle text-accent-text'
                    : 'border-line hover:bg-subtle'
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        )}

        <label className="flex flex-col gap-1 text-sm">
          {t('Time range')}
          <select
            value={jam}
            onChange={(e) => setJam(Number(e.target.value))}
            className="min-h-tap rounded-md border border-line bg-surface px-2"
          >
            {/* Nilainya datang dari `PILIHAN_JAM` supaya `lihatTeknis` tidak
                pernah memilih rentang yang tidak ada di daftar ini. */}
            <option value={PILIHAN_JAM[0]}>{t('Last hour')}</option>
            <option value={PILIHAN_JAM[1]}>{t('Last 24 hours')}</option>
            <option value={PILIHAN_JAM[2]}>{t('Last 3 days')}</option>
            <option value={PILIHAN_JAM[3]}>{t('Last 7 days')}</option>
            <option value={PILIHAN_JAM[4]}>{t('Last 30 days')}</option>
          </select>
        </label>

        {tabAktif === 'teknis' && (
        <label className="flex flex-col gap-1 text-sm">
          {t('Search messages')}
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('e.g. timeout')}
            className="min-h-tap w-48 rounded-md border border-line bg-surface px-3"
          />
        </label>
        )}

        {/* Export CSV hanya Admin (§D7.3), dan hanya di tab yang punya tabelnya. */}
        {isAdmin && tabAktif === 'teknis' && (
          <a
            href={exportHref}
            className="flex min-h-tap items-center rounded-md border border-line px-4 text-sm transition-colors hover:bg-subtle"
          >
            {t('Export CSV')}
          </a>
        )}
      </div>

      {/* Tab duduk DI BAWAH bilah filter dengan sengaja (§G13): filter kamera,
          rentang waktu, dan kedua kartu ringkasan milik halaman, bukan milik
          tab. Filter yang ter-reset saat berpindah tab membuat orang mengulang
          pekerjaannya. */}
      <div role="tablist" aria-label={t('Log view')} className="mb-4 flex gap-1 border-b border-line">
        {(['aktivitas', 'teknis'] as Tab[])
          // Tab Teknis memuat `stderr` mentah — Admin + Operator saja (§D7.3).
          // Disembunyikan sepenuhnya dari Viewer, bukan sekadar di-disable:
          // tab yang ada tapi tidak bisa dibuka hanya memberi tahu bahwa ada
          // sesuatu yang tidak boleh dilihatnya.
          .filter((k) => k === 'aktivitas' || isAdmin)
          .map((k) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={tabAktif === k}
              onClick={() => pilihTab(k)}
              className={`min-h-tap px-4 text-sm ${
                tabAktif === k
                  ? 'border-b-2 border-accent text-accent-text'
                  : 'border-b-2 border-transparent text-muted hover:bg-subtle'
              }`}
            >
              {k === 'aktivitas' ? t('Activity') : t('Technical')}
            </button>
          ))}
      </div>

      {error && (
        <p className="mb-4 text-sm text-amber-600" role="alert">
          {error}
        </p>
      )}

      {tabAktif === 'aktivitas' ? (
        cameraFilter.length === 0 ? (
          // Aktivitas selalu tentang SATU kamera: ia rangkaian keadaan satu
          // perangkat, dan menumpuk 96 rangkaian di satu halaman menghasilkan
          // sesuatu yang tidak bisa dibaca siapa pun. Kartu ringkasan di atas
          // sudah menjawab "ada yang rusak?"; ini menjawab "kamera ini kenapa".
          <div className="rounded-lg border border-dashed border-line p-10 text-center">
            <p className="text-lg">{t('Pick a camera to see what happened to it.')}</p>
            <p className="mt-1 text-sm text-muted">
              {t('The cards above show which cameras need looking at.')}
            </p>
          </div>
        ) : (
          <CameraActivity
            cameraId={cameraFilter[0]!}
            hours={jam}
            bolehCatat={isAdmin}
            onLihatTeknis={
              isAdmin ? (ep) => lihatTeknis(cameraFilter[0]!, ep) : undefined
            }
          />
        )
      ) : !data ? (
        <p className="text-sm text-muted" role="status">
          {t('Loading events…')}
        </p>
      ) : data.logs.length === 0 ? (
        <div className="rounded-lg border border-dashed border-line p-10 text-center">
          <p className="text-lg">{t('No events in this time range.')}</p>
          <p className="mt-1 text-sm text-muted">
            {t('Try widening the time range, or including more levels.')}
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-line">
            <table className="w-full text-sm">
              <thead className="bg-subtle text-left">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('Time (WIB)')}</th>
                  <th className="px-3 py-2 font-medium">{t('Camera')}</th>
                  <th className="px-3 py-2 font-medium">{t('Level')}</th>
                  <th className="px-3 py-2 font-medium">{t('Message')}</th>
                  <th className="px-3 py-2 text-right font-medium">{t('Count')}</th>
                </tr>
              </thead>
              <tbody>
                {data.logs.map((row) => {
                  const style = LEVEL_STYLE[row.level];
                  const dibuka = terbuka === row.id;
                  return (
                    // Fragment butuh key-nya sendiri; menaruh key di <tr> saja
                    // membuat React kehilangan identitas baris saat panel
                    // detail dibuka/ditutup.
                    <Fragment key={row.id}>
                      <tr
                        onClick={() => setTerbuka(dibuka ? null : row.id)}
                        className="cursor-pointer border-t border-line hover:bg-subtle"
                      >
                        <td className="px-3 py-2 align-top">
                          {formatWib(row.lastOccurredAt)}
                          {row.occurrenceCount > 1 && (
                            <div className="text-xs text-muted">
                              {t('since')} {formatWib(row.occurredAt)}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 align-top">{row.cameraName ?? row.cameraId}</td>
                        <td className="px-3 py-2 align-top">
                          <span
                            className={`rounded px-1.5 py-0.5 text-xs font-semibold ${style.chip}`}
                          >
                            {style.label}
                          </span>
                        </td>
                        <td className="px-3 py-2 align-top">
                          {row.message}
                          <div className="text-xs text-muted">{row.event}</div>
                        </td>
                        <td className="px-3 py-2 text-right align-top">
                          {row.occurrenceCount > 1 && (
                            <span className="rounded bg-subtle px-1.5 py-0.5 text-xs font-semibold">
                              ×{row.occurrenceCount}
                            </span>
                          )}
                        </td>
                      </tr>
                      {dibuka && (
                        <tr className="border-t border-line bg-subtle">
                          <td colSpan={5} className="px-3 py-3">
                            {/* Admin melihat stderr mentah; Viewer hanya pesan
                                yang sudah dipetakan (§D7.3). */}
                            {isAdmin && row.detail !== undefined ? (
                              <pre className="overflow-x-auto whitespace-pre-wrap break-words text-xs">
                                {JSON.stringify(row.detail, null, 2)}
                              </pre>
                            ) : (
                              <p className="text-sm">
                                {row.message}
                                <span className="ml-2 text-muted">
                                  {t('Technical details are available to administrators only.')}
                                </span>
                              </p>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex items-center justify-between gap-2">
            <span className="text-sm text-muted">
              {t('Page {page} of {pageCount} — {total} entries', {
                page: data.page + 1,
                pageCount: data.pageCount,
                total: data.total,
              })}
            </span>
            <div className="flex items-center gap-2">
              {/* Duduk bersebelahan dengan paging, bukan di bilah filter
                  di atas: ini kendali paging, dan tempat orang mencarinya
                  adalah tempat paging-nya berada. */}
              <label className="flex items-center gap-1.5 text-sm text-muted">
                {t('Rows')}
                <select
                  value={pageSize}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    setPageSize(n);
                    try {
                      localStorage.setItem(KUNCI_PAGE_SIZE, String(n));
                    } catch {
                      // Tidak bisa diingat bukan alasan untuk tidak berubah.
                    }
                  }}
                  aria-label={t('Rows per page')}
                  className="min-h-tap rounded-md border border-line bg-surface px-2 text-sm"
                >
                  {PILIHAN_PAGE_SIZE.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <NomorHalaman page={data.page} pageCount={data.pageCount} onPage={setPage} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
